class KabilaUtils {
    generateUUID() {
        return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
            var r = (Math.random() * 16) | 0,
                v = c == "x" ? r : (r & 0x3) | 0x8;
            return v.toString(16);
        });
    }
}

class KabilaLocalStorage {
    static KEYS = {
        UUID: "kabila_user_uuid",
        APPLIED_COUPONS: "kabila_applied_coupons",
    }

    static getItem(key) {
        if (!key) return null;
        let value = localStorage.getItem(key);
        return value;
    }

    static setItem(key, value) {
        if (!key) return null;
        localStorage.setItem(key, value);
    }

    static deleteItem(key) {
        if (!key) return null;
        localStorage.removeItem(key);
    }

    static getUUID() {
        const key = this.KEYS.UUID;
        return this.getItem(key);
    }

    static setUUID(value) {
        const key = this.KEYS.UUID;
        return this.setItem(key, value);
    }

    static getAppliedCoupons() {
        const key = this.KEYS.APPLIED_COUPONS;
        return JSON.parse(this.getItem(key) ?? "[]");
    }

    static setAppliedCoupons(list) {
        const key = this.KEYS.APPLIED_COUPONS;
        this.setItem(key, JSON.stringify(list));
    }
}

class KabilaSessionStorage {
    static KEYS = {
        UTM_ORIGIN: "kabila_utm_origin",
        UTM_SOURCE: "kabila_utm_source",
        PRODUCT_DISCOUNT_TIMER: "kabila_product_discount_timer",
    }

    static getItem(key) {
        if (!key) return null;
        let value = sessionStorage.getItem(key);
        return value;
    }

    static setItem(key, value) {
        if (!key) return null;
        sessionStorage.setItem(key, value);
    }

    static deleteItem(key) {
        if (!key) return null;
        sessionStorage.removeItem(key);
    }

    static setUTMOrigin(value) {
        const key = this.KEYS.UTM_ORIGIN;
        return this.setItem(key, value);
    }

    static getUTMOrigin() {
        const key = this.KEYS.UTM_ORIGIN;
        return this.getItem(key);
    }

    static setUTMSource(value) {
        const key = this.KEYS.UTM_SOURCE;
        return this.setItem(key, value);
    }

    static getUTMSource() {
        const key = this.KEYS.UTM_SOURCE;
        return this.getItem(key);
    }

    static setProductDiscountTimer(value) {
        const key = this.KEYS.PRODUCT_DISCOUNT_TIMER;
        return this.setItem(key, value);
    }

    static getProductDiscountTimer() {
        console.log("getProductDiscountTimer called");
        const key = this.KEYS.PRODUCT_DISCOUNT_TIMER;
        return this.getItem(key) ?? 1800;
    }
}

class KabilaGlobalApi {
    static getHeaders() {
        const myHeaders = new Headers();
        myHeaders.append("Content-Type", "application/json");

        const uid = KabilaLocalStorage.getUUID();
        if (uid) {
            myHeaders.append("uid", uid);
        }

        return myHeaders;
    }

    static getBaseUrl() {
        return KabilaConfig.getBaseUrl();
    }

    static async registerUser() {
        const requestOptions = {
            method: "POST",
            headers: this.getHeaders(),
        };

        return fetch(`${this.getBaseUrl()}/api/shopify/user/register`, requestOptions)
            .then((response) => response.json())
            .catch((error) => console.error(error));
    }

    static async getRecentPurchases() {
        const requestOptions = {
            method: "GET",
            headers: this.getHeaders(),
        };

        return fetch(`${this.getBaseUrl()}/api/shopify/order/get-recent`, requestOptions)
            .then((response) => response.json())
            .catch((error) => console.error(error));
    }

    static async getDiscountCoupons() {
        const requestOptions = {
            method: "GET",
            headers: this.getHeaders(),
        };

        return fetch(`${this.getBaseUrl()}/api/shopify/discount/get-coupons`, requestOptions)
            .then((response) => response.json())
            .catch((error) => console.error(error));
    }

    static async applyDiscountCoupon(discountCode) {
        const requestOptions = {
            method: "GET",
            headers: this.getHeaders(),
        };
        const cartId = await this.getCartId();

        if (!cartId) {
            console.error("Cart ID not found.");
            return;
        }

        return fetch(`${this.getBaseUrl()}/api/shopify/discount/apply?discountCode=${discountCode}&cartId=${cartId}`, requestOptions)
            .then((response) => response.json())
            .catch((error) => console.error(error));
    }

    static async removeDiscountCoupon(discountCode) {
        const requestOptions = {
            method: "GET",
            headers: this.getHeaders(),
        };
        const cartId = await this.getCartId();

        if (!cartId) {
            console.error("Cart ID not found.");
            return;
        }

        return fetch(`${this.getBaseUrl()}/api/shopify/discount/remove?discountCode=${discountCode}&cartId=${cartId}`, requestOptions)
            .then((response) => response.json())
            .catch((error) => console.error(error));
    }

    static async applyAppExclusiveDiscountCoupon(discountCode) {
        const requestOptions = {
            method: "GET",
            headers: this.getHeaders(),
        };
        const cartId = await this.getCartId();

        if (!cartId) {
            console.error("Cart ID not found.");
            return;
        }

        return fetch(`${this.getBaseUrl()}/api/shopify/discount/apply-app-exclusive-coupons?discountCode=${discountCode}&cartId=${cartId}`, requestOptions)
            .then((response) => response.json())
            .catch((error) => console.error(error));
    }

    static async getOrderDetailsByName(orderName) {
        const requestOptions = {
            method: "GET",
            headers: this.getHeaders(),
        };

        return fetch(`${this.getBaseUrl()}/api/shiprocket/details?orderName=${encodeURIComponent(orderName)}`, requestOptions)
            .then((response) => response.json())
            .catch((error) => console.error(error));
    }

    static async getCartId() {
        const MAX_RETRIES = 10;
        // Try to getch cart token, if cart token's lenght is less than 10, then keep on trying it 5 times with a delay of 1 second. If still not found, then return null.
        let cart = await ShopifyAjaxAPI.getCart();
        let cartToken = cart?.token;
        let tries = 0;
        while ((!cartToken || ((cartToken?.length ?? 0) <= 32)) && tries < MAX_RETRIES) {
            await new Promise((resolve) => setTimeout(resolve, 1000));
            cart = await ShopifyAjaxAPI.getCart();
            cartToken = cart?.token;
            tries++;
        }

        return cartToken;
    }
}